import { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { ClassChatSidebarPanel } from "../course/ClassChatSidebarPanel";
import { CourseUsersSidebarPanel } from "../course/CourseUsersSidebarPanel";
import { useIsAssessing } from "../course/assessment/hooks";
import { InstructorNotesSidebarPanel } from "../sidebar/InstructorNotesSidebarPanel";

import { AssessmentDisplayView } from "./assessment/AssessmentDisplayView";
import { AssessmentGradingView } from "./assessment/AssessmentGradingView";
import { PageOutcomeRubricSidebar } from "./outcomes/PageOutcomeRubricSidebar";

import { AIChatPanel } from "components/ai/components/AIChatPanel";
import {
  FOOTER_SECONDARY_OVERLAYS,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
  SecondaryOverlayType,
} from "components/constants.ts";
import { AssessmentSummary } from "components/materials/page/assessment/AssessmentSummary";
import { BreakoutsControlPanel } from "components/materials/sidebar/BreakoutsControlPanel";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectAssessmentBySubmissionId,
  selectIsRecordingAgent,
  selectShouldShowAssessmentSummary,
} from "store/selectors";
import { useQueryParamsManager } from "utils/routing";

type OverlayComponentProps = {
  overlay: SecondaryOverlayType;
  page?: PageType;
};

const OverlayComponent: FC<OverlayComponentProps> = ({ overlay, page }) => {
  switch (overlay) {
    case SecondaryOverlay.Outcomes:
      return page ? <PageOutcomeRubricSidebar pageId={page.id} /> : null;

    case SecondaryOverlay.AssessmentSummary:
      return page ? <AssessmentSummary page={page} /> : null;

    case SecondaryOverlay.BreakoutSettings:
      return <BreakoutsControlPanel />;

    case SecondaryOverlay.AIAssistant:
      return <AIChatPanel />;

    case SecondaryOverlay.Users:
      return <CourseUsersSidebarPanel />;

    case SecondaryOverlay.InstructorNotes:
      return <InstructorNotesSidebarPanel />;

    case SecondaryOverlay.ClassChat:
      return <ClassChatSidebarPanel />;

    default:
      return null;
  }
};

const OverlaySidebar: FC<{
  className?: string;
  title?: string;
  children: ReactNode | ReactNode[];
}> = ({ className, children, title }) => (
  <aside aria-label={title || t("glossary.secondary_sidebar")} className={className}>
    {children}
  </aside>
);

type PageSecondarySidebarProps = {
  className?: string;
  shouldShowOverlay?: boolean;
  page?: PageType;
};

export const PageSecondarySidebar: FC<PageSecondarySidebarProps> = ({
  className,
  page,
  shouldShowOverlay,
}) => {
  const { getQueryParam } = useQueryParamsManager();

  const { canAuthorCourse } = useCapabilities();
  const isAssessing = useIsAssessing();

  const secondaryOverlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const shouldShowAssessmentSummary = useAppSelector(selectShouldShowAssessmentSummary);
  const overlay = isRecordingAgent
    ? SecondaryOverlay.ClassChat
    : shouldShowAssessmentSummary
      ? SecondaryOverlay.AssessmentSummary
      : secondaryOverlay;
  const assessment = useAppSelector((s) => selectAssessmentBySubmissionId(s, submissionId)) || null;
  const showingFooterSecondaryOverlay = FOOTER_SECONDARY_OVERLAYS.includes(secondaryOverlay);

  // Need to add an overlay backdrop on the page secondary sidebar area if the sidebar
  // isn't accessible via the footer.
  const overlayBackdrop = shouldShowOverlay ? (
    <div className="absolute inset-0 z-10 bg-black-tint-95/80 backdrop-blur-sm" />
  ) : null;

  if (overlay) {
    return (
      <OverlaySidebar
        className={twMerge(
          className,
          // Since this also includes secondary sidebars not accessible via
          // the footer, we want to only blur the background (and not the
          // content) if there is a footer-accessible secondary sidebar.
          shouldShowOverlay &&
            showingFooterSecondaryOverlay &&
            "bg-black-tint-95/80 backdrop-blur-sm",
        )}
      >
        <OverlayComponent overlay={overlay} page={page} />
        {/* Otherwise, we will blur the entire secondary sidebar. */}
        {!showingFooterSecondaryOverlay && overlayBackdrop}
      </OverlaySidebar>
    );
  }

  if (isAssessing && page) {
    return (
      <OverlaySidebar
        className={twMerge(className, "px-0")}
        title={t("assessment.assessment_panel")}
      >
        <AssessmentGradingView page={page} />
        {overlayBackdrop}
      </OverlaySidebar>
    );
  }

  const isViewingAssessment =
    assessment && page && (canAuthorCourse || page.assessments_published_at);
  if (isViewingAssessment) {
    return (
      <OverlaySidebar className={className}>
        <AssessmentDisplayView page={page} />
        {overlayBackdrop}
      </OverlaySidebar>
    );
  }

  return null;
};
